/**
 * CheckSelectBox Field Renderer
 *
 * Airbnb-style grid selection with icons
 * Renders options as grid cards with icons, supporting both single and multi-selection
 * Features smooth animations and customizable grid layout
 */

import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import type { FieldRenderer } from '@/MyCode/core';

/**
 * CheckSelectBox Renderer
 * Features:
 * - Grid layout with customizable columns (1-6)
 * - Single or multi-selection mode
 * - Icon support from Lucide icons
 * - Custom content support
 * - Stagger animations with configurable intensity
 * - Visual feedback with check marks
 * - Accessible with focus states
 */
export const renderCheckSelectBox: FieldRenderer = ({ field, value, hasError, handleChange, renderIcon, isRequired, animConfig, shouldAnimate }) => {
    const gridCols = field.ui?.gridCols || 3;
    const multiSelect = field.ui?.multiSelect !== false; // Default to true for backward compatibility

    // Handle both single and multi-select values
    const selectedValues = multiSelect
        ? (Array.isArray(value) ? value : (value ? [value] : []))
        : [];
    const selectedValue = !multiSelect ? (typeof value === 'string' ? value : '') : '';

    const toggleSelection = (itemValue: string) => {
        if (multiSelect) {
            // Multi-select mode: toggle item in array
            let newValues: string[];
            if (selectedValues.includes(itemValue)) {
                // Remove if already selected
                newValues = selectedValues.filter(v => v !== itemValue);
            } else {
                // Add if not selected
                newValues = [...selectedValues, itemValue];
            }
            handleChange(field.name, newValues);
        } else {
            // Single-select mode: set single value (or clear if clicking same item)
            const newValue = selectedValue === itemValue ? '' : itemValue;
            handleChange(field.name, newValue);
        }
    };

    // Grid column classes mapping
    const gridColsClass: Record<number, string> = {
        1: 'grid-cols-1',
        2: 'grid-cols-2',
        3: 'grid-cols-3',
        4: 'grid-cols-4',
        5: 'grid-cols-5',
        6: 'grid-cols-6',
    };

    const getGridClass = () => gridColsClass[gridCols] || 'grid-cols-3';

    // Animation intensity configurations
    const animationIntensity = {
        subtle: { scale: 1.02, rotate: 0 },
        medium: { scale: 1.05, rotate: 2 },
        bold: { scale: 1.08, rotate: 5 },
    };

    const intensity = animConfig?.checkSelectBoxIntensity || 'subtle';
    const { scale, rotate } = animationIntensity[intensity];

    return (
        <div className="space-y-2">
            <Label>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <motion.div
                className={`grid ${getGridClass()} gap-3`}
                variants={{
                    hidden: { opacity: 0 },
                    show: {
                        opacity: 1,
                        transition: {
                            staggerChildren: shouldAnimate ? 0.08 : 0
                        }
                    }
                }}
                initial="hidden"
                animate="show"
            >
                {field.options?.items?.map((option) => {
                    const isSelected = multiSelect
                        ? selectedValues.includes(option.value)
                        : selectedValue === option.value;
                    const displayTitle = option.title || option.label;
                    const Icon = option.icon ? (LucideIcons as any)[option.icon] : null;

                    return (
                        <motion.button
                            key={option.value}
                            type="button"
                            onClick={() => toggleSelection(option.value)}
                            variants={{
                                hidden: { opacity: 0, scale: 0.8 },
                                show: { opacity: 1, scale: 1 }
                            }}
                            whileHover={shouldAnimate ? { scale: scale } : {}}
                            whileTap={shouldAnimate ? { scale: 0.95, rotate: rotate } : {}}
                            transition={{
                                duration: animConfig ? animConfig.duration / 1000 : 0.3,
                                ease: "easeOut"
                            }}
                            className={`
                                relative flex flex-col items-center justify-center p-6 rounded-xl border-2 transition-colors duration-200
                                ${isSelected
                                    ? 'border-primary bg-primary/5 shadow-md'
                                    : 'border-input hover:border-primary/50 hover:bg-accent/50'
                                }
                                ${hasError ? 'border-destructive' : ''}
                                focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
                            `}
                        >
                            {/* Custom content if provided */}
                            {option.customContent ? (
                                <>
                                    {option.customContent}
                                    {/* Check icon when selected for custom content */}
                                    {isSelected && (
                                        <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                                            <LucideIcons.Check size={14} strokeWidth={3} />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    {/* Default layout: Icon */}
                                    {Icon && (
                                        <div className={`mb-3 align-top content-start ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                                            <Icon size={32} strokeWidth={1.5} />
                                        </div>
                                    )}

                                    {/* Title */}
                                    <div className={`text-center font-medium text-sm ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                                        {displayTitle}
                                    </div>

                                    {/* Description (label if different from title) */}
                                    {option.title && option.label !== option.title && (
                                        <div className="text-xs text-muted-foreground mt-1 text-center">
                                            {option.label}
                                        </div>
                                    )}

                                    {/* Check icon when selected */}
                                    {isSelected && (
                                        <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                                            <LucideIcons.Check size={14} strokeWidth={3} />
                                        </div>
                                    )}
                                </>
                            )}
                        </motion.button>
                    );
                })}
            </motion.div>
        </div>
    );
};

export default renderCheckSelectBox;
